import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { Button, FormControl, InputLabel, responsiveFontSizes, TextField } from '@material-ui/core';
import { addDays, format, subMonths } from 'date-fns';
import ReactInputMask from 'react-input-mask';
import { useMemo } from 'react';
import { createChildOrder, resetInVisionOrder } from '../../../services';
import { useOrderDetailContext } from '../OrderDetailContext';
import Loading from '../../Loading';
import { AppContext } from '../../../AppContext';
import { cloneDeep } from 'lodash';

const styles = theme => ({
  formControl: {
    marginTop: 10,

    '&:first-of-type': {
      marginTop: 0,
    },
  },

  input: {
    'label + &': {
      marginTop: theme.spacing(2),
    },
    '& .MuiInputBase-input': {
      borderRadius: 4,
      position: 'relative',
      fontSize: 16,
      padding: '10px 12px',
      transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    },
  },
});

const FIELDS = {
  INSURED_PHONE: 'insuredPhone1',
  INSURED_NAME: 'policyHolderName',
  INSURED_ADDRESS: 'insuredaddress',
  POLICY_NUMBER: 'policy',
  HOMEOWNER_DUE_DATE: 'homeownerDueDate',
  AGENT_CODE: 'agentCode',
  AGENT_NAME: 'agentName',
  AGENT_PHONE: 'agentPhone',
  AGENT_EMAIL: 'agentEmail',
};

const QuickVueVirtualToInvisionConversionButton = withStyles(styles)(
  ({
    className,
    classes,
    orderLineId,
    fetchOrderData,
    disabled,
    onClick,
    onHide,
    children,
    orders: initialOrders = [],
    showDialog,
  }) => {
    // #region STATE
    const [introAccepted, setIntroAccepted] = useState(false);
    const [orders, setOrders] = useState([]);
    const [currentOrderIndex, setCurrentOrderIndex] = useState(0);
    const [formIsSaving, setFormIsSaving] = useState(false);
    const [formError, setFormError] = useState(null);
    const [formSuccess, setFormSuccess] = useState(false);
    // #endregion

    // #region HOOKS
    const { dispatch } = useContext(AppContext);
    // #endregion

    // #region FUNCTIONS
    const hideDialog = () => {
      !!onHide && onHide();
    };

    const handleInputUpdate = field => evt => {
      const updatedOrders = cloneDeep(orders);
      updatedOrders[currentOrderIndex][field] = evt.target.value;
      setOrders(updatedOrders);
    };

    const handlePrevious = () => {
      if (currentOrderIndex > 0) {
        setCurrentOrderIndex(currentOrderIndex - 1);
      }
    };

    const handleNext = async () => {
      if (!introAccepted) {
        setIntroAccepted(true);
        return;
      }

      try {
        setFormIsSaving(true);
        setFormError(null);

        const {
          orderLineId,
          insuredPhone1,
          insuredName,
          policy,
          homeownerDueDate,
          agentCode,
          agentName,
          agentPhone,
          agentEmail,
        } = currentOrder;

        const orderDetails = {
          parentOrderLineId: orderLineId,
          reportCode: 'QVUEL',

          insuredPhone1,
          insuredName,
          policy,
          homeownerDueDate,
          agentCode,
          agentName,
          agentPhone,
          agentEmail,
        };

        await createChildOrder(orderDetails);
        // await new Promise(resolve => setTimeout(resolve, 1000));

        setFormIsSaving(false);
        setFormSuccess(true);

        await new Promise(resolve => setTimeout(resolve, 500));

        setFormSuccess(false);

        if (currentOrderIndex === orders.length - 1) {
          !!onHide && onHide();
        } else {
          setCurrentOrderIndex(currentOrderIndex + 1);
        }

        // fetchOrderData();
      } catch (error) {
        console.log(error);
        setFormError(error.Message || error.ErrorMessage || 'Something went wrong.  Please try again.');
      } finally {
        setFormIsSaving(false);
      }
    };

    const orderIsValid = order => {
      const insuredPhoneValid = order[FIELDS.INSURED_PHONE]?.replace(/[-|_() ]/g, '').length === 10; // filter out dashes and underscrores from mask
      const dueDateValid = !!order[FIELDS.HOMEOWNER_DUE_DATE];

      const agentCodeValid = !!order[FIELDS.AGENT_CODE];
      const agentNameValid = !!order[FIELDS.AGENT_NAME];
      const agentEmailValid = !!order[FIELDS.AGENT_EMAIL];
      const agentPhoneValid = order[FIELDS.AGENT_PHONE]?.replace(/[-|_() ]/g, '').length === 10; // filter out dashes and underscrores from mask

     // console.log({ insuredPhoneValid, dueDateValid, agentCodeValid, agentNameValid, agentEmailValid, agentPhoneValid })
      if (!insuredPhoneValid) {
        setFormError('Please enter a valid insured phone number.');
      }
      if (!dueDateValid) {
        setFormError('Please enter a due date.');
      }
      if (!agentCodeValid) {
        setFormError('Please enter an valid agent code.');
      }
      if (!agentNameValid) {
        setFormError('Please enter a valid agent name.');
      }
      if (!agentEmailValid) {
        setFormError('Please enter a valid agent email.');
      }
      if (!agentPhoneValid) {
        setFormError('Please enter a valid agent phone number.');
      }

      return [
        //
        insuredPhoneValid,
        dueDateValid,
        //
        agentCodeValid,
        agentNameValid,
        agentEmailValid,
        agentPhoneValid,
      ].every(Boolean);


    };

    // #endregion

    // #region COMPUTED
    const currentOrder = useMemo(() => {
      return orders[currentOrderIndex] || {};
    }, [orders, currentOrderIndex]);

    const currentOrderIsValid = useMemo(() => {
      return orderIsValid(currentOrder);
    }, [currentOrder]);

    // #endregion

    // #region EFFECTS
    useEffect(() => {
      if (showDialog) {
        setOrders(
          initialOrders.map(order => ({
            ...order,
            [FIELDS.HOMEOWNER_DUE_DATE]: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
          })),
        );
        setCurrentOrderIndex(0);
      }
    }, [showDialog]);
    // #endregion

    return (
      <>
        <ConfirmationDialog
          visible={showDialog}
          setVisibility={hideDialog}
          title={`Virtual Order Conversion${introAccepted ? ` - ${currentOrderIndex + 1}/${orders.length}` : ''}`}
          subtitle={introAccepted && `${currentOrder[FIELDS.POLICY_NUMBER]} (${currentOrder[FIELDS.INSURED_NAME]})  (${currentOrder[FIELDS.INSURED_ADDRESS]})`}
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
          minHeight={1}
          isSubmitting={formIsSaving}
          submitMessage={
            formSuccess ? (
              <div
                style={{
                  color: 'var(--jmi-green)',
                  textAlign: 'center',
                }}
              >
                Success!
              </div>
            ) : (
              introAccepted && (
                <div
                  style={{
                    color: 'var(--jmi-red)',
                    textAlign: 'center',
                  }}
                >
                  {formError}
                </div>
              )
            )
          }
          cancelButton={
            <Button
              variant="contained"
              onClick={hideDialog}
              style={{
                backgroundColor: 'var(--jmi-red)',
                color: 'white',
              }}
            >
              Cancel
            </Button>
          }
          acceptButton={
            <>
              {/* {!!currentOrderIndex && (
                <Button
                  variant="contained"
                  onClick={handlePrevious}
                  style={{
                    backgroundColor: 'var(--jmi-blue)',
                    color: 'white',
                    marginRight: 10,
                  }}
                >
                  <span
                    style={{
                      opacity: formIsSaving ? 0.35 : 1,
                    }}
                  >
                    Previous
                  </span>
                </Button>
              )} */}

              <Button
                variant="contained"
                disabled={(introAccepted && !currentOrderIsValid) || formIsSaving || formSuccess}
                onClick={handleNext}
                style={{
                  backgroundColor: !introAccepted || currentOrderIsValid ? 'var(--jmi-blue)' : undefined,
                  color: !introAccepted || currentOrderIsValid ? 'white' : undefined,
                }}
              >
                <span
                  style={{
                    opacity: formIsSaving ? 0.35 : 1,
                  }}
                >
                  {currentOrderIndex < orders.length - 1 ? 'Next' : !introAccepted ? 'Yes, Continue' : 'Submit'}
                </span>

                {formIsSaving && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      bottom: 0,
                      left: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Loading
                      style={{
                        height: 25,
                        width: 25,
                        color: 'white',
                      }}
                    />
                  </div>
                )}
              </Button>
            </>
          }
        >
          {introAccepted ? (
            <div className="flex-column" key={`order-form-index-${currentOrderIndex}`}>
              <FormControl variant="standard" className={classes.formControl}>
                <InputLabel shrink htmlFor={FIELDS.INSURED_PHONE}>
                  Insured Phone Number
                </InputLabel>

                <ReactInputMask
                  mask="999-999-9999"
                  value={currentOrder[FIELDS.INSURED_PHONE]}
                  onChange={handleInputUpdate(FIELDS.INSURED_PHONE)}
                  disabled={false}
                  maskChar="_"
                >
                  {() => (
                    <TextField
                      //
                      type="text"
                      name={FIELDS.INSURED_PHONE}
                      variant="outlined"
                      className={classes.input}
                    />
                  )}
                </ReactInputMask>
              </FormControl>

              <FormControl variant="standard" className={classes.formControl}>
                <InputLabel shrink htmlFor={FIELDS.HOMEOWNER_DUE_DATE}>
                  Due Date
                </InputLabel>
                <TextField
                  type="date"
                  name={FIELDS.HOMEOWNER_DUE_DATE}
                  variant="outlined"
                  value={currentOrder[FIELDS.HOMEOWNER_DUE_DATE]}
                  onChange={handleInputUpdate(FIELDS.HOMEOWNER_DUE_DATE)}
                  className={classes.input}
                />
              </FormControl>

              <FormControl variant="standard" className={classes.formControl}>
                <InputLabel shrink htmlFor={FIELDS.AGENT_NAME}>
                  Agent Name
                </InputLabel>
                <TextField
                  type="text"
                  name={FIELDS.AGENT_NAME}
                  variant="outlined"
                  value={currentOrder[FIELDS.AGENT_NAME]}
                  onChange={handleInputUpdate(FIELDS.AGENT_NAME)}
                  className={classes.input}
                />
              </FormControl>

              <FormControl variant="standard" className={classes.formControl}>
                <InputLabel shrink htmlFor={FIELDS.AGENT_CODE}>
                  Agent Code
                </InputLabel>
                <TextField
                  type="text"
                  name={FIELDS.AGENT_CODE}
                  variant="outlined"
                  value={currentOrder[FIELDS.AGENT_CODE]}
                  onChange={handleInputUpdate(FIELDS.AGENT_CODE)}
                  className={classes.input}
                />
              </FormControl>

              <FormControl variant="standard" className={classes.formControl}>
                <InputLabel shrink htmlFor={FIELDS.AGENT_PHONE}>
                  Agent Phone Number
                </InputLabel>

                <ReactInputMask
                  mask="999-999-9999"
                  value={currentOrder[FIELDS.AGENT_PHONE]}
                  onChange={handleInputUpdate(FIELDS.AGENT_PHONE)}
                  disabled={false}
                  maskChar="_"
                >
                  {() => (
                    <TextField
                      //
                      type="text"
                      name={FIELDS.AGENT_PHONE}
                      variant="outlined"
                      className={classes.input}
                    />
                  )}
                </ReactInputMask>
              </FormControl>

              <FormControl variant="standard" className={classes.formControl}>
                <InputLabel shrink htmlFor={FIELDS.AGENT_EMAIL}>
                  Agent Email
                </InputLabel>
                <TextField
                  type="text"
                  name={FIELDS.AGENT_EMAIL}
                  variant="outlined"
                  value={currentOrder[FIELDS.AGENT_EMAIL]}
                  onChange={handleInputUpdate(FIELDS.AGENT_EMAIL)}
                  className={classes.input}
                />
              </FormControl>
            </div>
          ) : (
            <h3 className="text-center">
              You are about to convert virtual order(s) to QuickVUE order(s). This action is irreversible. Are you sure
              you want to continue?
            </h3>
          )}
        </ConfirmationDialog>

        <Button
          onClick={onClick}
          variant="contained"
          className={className}
          style={{
            backgroundColor: 'var(--jmi-blue)',
            color: 'white',
          }}
        >
          {children}
        </Button>
      </>
    );
  },
);

QuickVueVirtualToInvisionConversionButton.propTypes = {
  className: PropTypes.object,
  onSave: PropTypes.func,
};

export default QuickVueVirtualToInvisionConversionButton;
